// =====================================================
// ENUMS
// =====================================================

export const PAYMENT_SCOPE = {
  BPI: 'bpi',
  BED: 'bed',
} as const;

export type PaymentScope = (typeof PAYMENT_SCOPE)[keyof typeof PAYMENT_SCOPE];

export const PAYMENT_STATUS = {
  DRAFT: 'draft',
  PENDING_CARD: 'pending_card',
  PENDING_TRANSFER: 'pending_transfer',
  PENDING_CHEQUE: 'pending_cheque',
  PARTIAL: 'partial',
  PAID: 'paid',
  FAILED: 'failed',
  EXPIRED: 'expired',
} as const;

export type PaymentStatus = (typeof PAYMENT_STATUS)[keyof typeof PAYMENT_STATUS];

export const PAYMENT_METHOD = {
  CARD: 'card',
  TRANSFER: 'transfer',
  CHEQUE: 'cheque',
} as const;

export type PaymentMethod = (typeof PAYMENT_METHOD)[keyof typeof PAYMENT_METHOD];

// =====================================================
// LABELS
// =====================================================

export const PAYMENT_SCOPE_LABELS: Record<PaymentScope, string> = {
  [PAYMENT_SCOPE.BPI]: 'BPI',
  [PAYMENT_SCOPE.BED]: 'BED',
};

export const PAYMENT_STATUS_LABELS: Record<PaymentStatus, string> = {
  [PAYMENT_STATUS.DRAFT]: 'En attente de choix',
  [PAYMENT_STATUS.PENDING_CARD]: 'En attente CB',
  [PAYMENT_STATUS.PENDING_TRANSFER]: 'En attente virement',
  [PAYMENT_STATUS.PENDING_CHEQUE]: 'En attente chèque',
  [PAYMENT_STATUS.PARTIAL]: 'Partiellement payé',
  [PAYMENT_STATUS.PAID]: 'Payé',
  [PAYMENT_STATUS.FAILED]: 'Échoué',
  [PAYMENT_STATUS.EXPIRED]: 'Expiré',
};

export const PAYMENT_METHOD_LABELS: Record<PaymentMethod, string> = {
  [PAYMENT_METHOD.CARD]: 'Carte bancaire',
  [PAYMENT_METHOD.TRANSFER]: 'Virement',
  [PAYMENT_METHOD.CHEQUE]: 'Chèque',
};

// =====================================================
// COLORS
// =====================================================

export const PAYMENT_STATUS_COLORS: Record<
  PaymentStatus,
  { bg: string; text: string; border: string }
> = {
  [PAYMENT_STATUS.DRAFT]: {
    bg: 'bg-gray-50',
    text: 'text-gray-700',
    border: 'border-gray-200',
  },
  [PAYMENT_STATUS.PENDING_CARD]: {
    bg: 'bg-blue-50',
    text: 'text-blue-700',
    border: 'border-blue-200',
  },
  [PAYMENT_STATUS.PENDING_TRANSFER]: {
    bg: 'bg-amber-50',
    text: 'text-amber-700',
    border: 'border-amber-200',
  },
  [PAYMENT_STATUS.PENDING_CHEQUE]: {
    bg: 'bg-orange-50',
    text: 'text-orange-700',
    border: 'border-orange-200',
  },
  [PAYMENT_STATUS.PARTIAL]: {
    bg: 'bg-indigo-50',
    text: 'text-indigo-700',
    border: 'border-indigo-200',
  },
  [PAYMENT_STATUS.PAID]: {
    bg: 'bg-green-50',
    text: 'text-green-700',
    border: 'border-green-200',
  },
  [PAYMENT_STATUS.FAILED]: {
    bg: 'bg-red-50',
    text: 'text-red-700',
    border: 'border-red-200',
  },
  [PAYMENT_STATUS.EXPIRED]: {
    bg: 'bg-zinc-50',
    text: 'text-zinc-700',
    border: 'border-zinc-200',
  },
};

// =====================================================
// INTERFACES
// =====================================================

export interface PaymentCase {
  id: string;
  scope: PaymentScope;
  product_id: string;
  contact_id: string | null;
  crew_id: string | null;
  payer_contact_id: string | null;
  form_submission_id: string | null;
  bpi_code: string | null;
  amount_cents: number;
  currency: string;
  status: PaymentStatus;
  method: PaymentMethod | null;
  order_id: string | null;
  email_sent_at: string | null; // ISO 8601
  created_at: string; // ISO 8601
  updated_at: string; // ISO 8601
}

export interface PaymentAttempt {
  id: string;
  payment_case_id: string;
  method: PaymentMethod;
  provider: string;
  provider_order_id: string | null;
  provider_transaction_id: string | null;
  provider_status: string | null;
  amount_cents: number;
  // Colonne migration 00079 : tentative effectivement encaissée (IPN PAID).
  // Le « reste à payer » BED multi-CB se calcule sur succeeded===true uniquement
  // — une tentative en attente ou refusée ne réduit pas le solde.
  succeeded: boolean;
  raw_ipn_payload: Record<string, unknown> | null;
  created_at: string; // ISO 8601
}
